import { cn, GlassAvatar, GlassButton } from '@merritrac/shared/components';
import { Moon, Sun } from 'lucide-react';
import { NavLink } from 'react-router';

import { usePortalAuth } from '../../hooks/usePortalAuth.js';
import { useTheme } from '../../hooks/useTheme.js';

import type { ReactElement } from 'react';

interface PortalUser {
  name: string;
  clientName: string;
}

interface PortalHeaderProps {
  user: PortalUser;
  onLogout: () => void;
}

export function PortalHeader({ user, onLogout }: PortalHeaderProps): ReactElement {
  const { theme, toggle } = useTheme();
  const portalRole = usePortalAuth((s) => s.user?.portalRole ?? 'property_manager');

  const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
    cn(
      'text-sm font-medium px-3 py-1.5 rounded-lg transition-all duration-[var(--dur)] ease-[var(--ease)]',
      isActive
        ? 'glass text-text shadow-[var(--glass-shadow)]'
        : 'text-text-secondary hover:text-text hover:bg-[var(--glass-bg)]',
    );

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-20 flex items-center justify-between',
        'h-[var(--header-h)] px-6',
        'bg-[var(--glass-bg)] backdrop-blur-[var(--glass-blur-medium)]',
        'border-b border-[var(--glass-border-subtle)]',
      )}
    >
      {/* Logo */}
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20 shadow-[0_0_12px_var(--primary)]">
          <svg
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            className="w-5 h-5 text-primary"
          >
            <line x1="16" y1="4" x2="16" y2="28" />
            <line x1="4" y1="16" x2="28" y2="16" />
            <line x1="7" y1="7" x2="25" y2="25" />
            <line x1="25" y1="7" x2="7" y2="25" />
          </svg>
        </div>
        <span className="text-sm font-semibold text-text">MerriTrac</span>
      </div>

      {/* Navigation — role-dependent */}
      <nav className="flex items-center gap-1 p-1 rounded-lg bg-[var(--glass-bg-subtle)] border border-[var(--glass-border-subtle)]">
        {portalRole === 'property_manager' ? (
          <>
            <NavLink to="/" className={navLinkClass} end>
              Orders
            </NavLink>
            <NavLink to="/contacts" className={navLinkClass}>
              Contacts
            </NavLink>
            <NavLink to="/work-order/new" className={navLinkClass}>
              New Order
            </NavLink>
            <NavLink to="/documents" className={navLinkClass}>
              Documents
            </NavLink>
          </>
        ) : (
          <>
            <NavLink to="/my-requests" className={navLinkClass} end>
              My Requests
            </NavLink>
            <NavLink to="/my-requests/new" className={navLinkClass}>
              New Request
            </NavLink>
          </>
        )}
      </nav>

      {/* User info + theme + logout */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          className="flex items-center justify-center w-8 h-8 rounded-lg text-text-secondary hover:text-text hover:bg-[var(--glass-bg)] transition-all cursor-pointer"
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
        </button>
        <div className="flex items-center gap-2">
          <GlassAvatar name={user.name || 'User'} size="sm" />
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium text-text leading-tight">{user.name}</span>
            <span className="text-[11px] text-text-secondary leading-tight">{user.clientName}</span>
          </div>
        </div>
        <GlassButton variant="ghost" size="sm" onClick={onLogout}>
          Sign Out
        </GlassButton>
      </div>
    </header>
  );
}
